import { useMemo } from "react";

export type InputRegexFormatterTypes =
  | ("letters" | "numbers" | "spaces" | "upper" | "lower")[]
  | string[];

/**
 * Custom hook to create a function that validates an input value based on allowed types and specific characters using regex.
 *
 * @param {InputRegexFormatterTypes} formatterRules - An array of validation options specifying allowed character types and specific characters.
 * @returns {function(value: any): string} - A function that validates the input value based on the allowed types.
 */
export const useInputRegexFormatter = (
  formatterRules: InputRegexFormatterTypes,
): ((value: any) => string) => {
  // Memoize the regex pattern to avoid recalculating on every render
  const validInputRegex = useMemo(() => {
    // No filtering needed if no allowed types
    if (!formatterRules.length) return null;

    // Create regex pattern based on selected options
    let regex = "";

    // Define character groups based on the selected option
    if (formatterRules.includes("letters")) {
      regex += "a-zA-Z";
    }
    if (formatterRules.includes("numbers")) {
      regex += "0-9";
    }
    if (formatterRules.includes("spaces")) {
      regex += "\\s"; // All characters including whitespace
    }

    // Add specific characters if provided
    const specificChars = formatterRules.filter(
      (option) =>
        typeof option === "string" &&
        !["letters", "numbers", "spaces", "upper", "lower"].includes(option),
    );

    if (specificChars.length > 0) {
      // Escape each character in specificChars and append to regex
      const escapedChars = specificChars
        .map((char) => char.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")) // Escape regex special characters
        .join("");
      regex += escapedChars;
    }

    // Create and return the regex pattern
    return new RegExp(`[^${regex}]`, "g");
  }, [formatterRules]);

  // Return the validation function
  return (value: string): string => {
    if (formatterRules.includes("upper")) value = value.toUpperCase();
    if (formatterRules.includes("lower")) value = value.toLowerCase();
    if (!validInputRegex || typeof value !== "string") {
      return value; // Return the original value if no regex is applied
    }
    return value.replace(validInputRegex, ""); // Remove invalid characters
  };
};
